/**
 * Document Viewer Component
 * Displays formatted document content with markdown rendering
 */

import {StudyDocument} from '@voxapilot/shared';
import {cn} from '../../utils/cn';

interface DocumentViewerProps {
  document: StudyDocument;
  className?: string;
}

export function DocumentViewer({document, className}: DocumentViewerProps) {
  const content = document.formattedContent || document.extractedText || '';
  const hasAnalysis = document.analysis && document.analysis.keyConcepts.length > 0;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Document Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h1 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">
          {document.title}
        </h1>
        <div className="flex items-center gap-4 text-sm text-textSecondary dark:text-[#858585]">
          <span>
            {document.metadata?.pageCount ? `${document.metadata.pageCount} pages` : 'Processing...'}
          </span>
          {document.metadata?.topics && document.metadata.topics.length > 0 && (
            <span>• {document.metadata.topics.length} topics</span>
          )}
          <span>• {new Date(document.createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Key Concepts (if analyzed) */}
      {hasAnalysis && document.analysis && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-orange-900 dark:text-orange-200 mb-2">
            Key Concepts
          </h2>
          <div className="flex flex-wrap gap-2">
            {document.analysis.keyConcepts.slice(0, 10).map((concept, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 rounded text-xs font-medium"
              >
                {concept}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Document Content */}
      <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-textPrimary dark:prose-headings:text-[#CCCCCC] prose-p:text-textSecondary dark:prose-p:text-[#858585] prose-a:text-orange-600 dark:prose-a:text-orange-500 prose-strong:text-textPrimary dark:prose-strong:text-[#CCCCCC] prose-code:text-textPrimary dark:prose-code:text-[#CCCCCC] prose-code:bg-gray-100 dark:prose-code:bg-[#252526] prose-pre:bg-gray-900 dark:prose-pre:bg-[#1E1E1E] prose-ul:text-textSecondary dark:prose-ul:text-[#858585] prose-ol:text-textSecondary dark:prose-ol:text-[#858585]">
        <div className="whitespace-pre-wrap break-words">
          {content.split('\n').map((line, index) => {
            // Simple markdown parsing for headings
            if (line.startsWith('## ')) {
              return (
                <h2 key={index} className="text-xl font-bold mt-6 mb-3">
                  {line.replace('## ', '')}
                </h2>
              );
            }
            if (line.startsWith('### ')) {
              return (
                <h3 key={index} className="text-lg font-semibold mt-4 mb-2">
                  {line.replace('### ', '')}
                </h3>
              );
            }
            if (line.startsWith('#### ')) {
              return (
                <h4 key={index} className="text-base font-semibold mt-3 mb-2">
                  {line.replace('#### ', '')}
                </h4>
              );
            }
            if (line.startsWith('- ') || line.startsWith('* ')) {
              return (
                <li key={index} className="ml-4">
                  {line.replace(/^[-*] /, '')}
                </li>
              );
            }
            if (line.trim() === '') {
              return <br key={index} />;
            }
            return (
              <p key={index} className="mb-3 leading-relaxed">
                {line}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}

